<template lang="pug">
  .b-spec-row(data-testid="spec-row" :class="{ readonly: isReadOnly, selected, highlighted }" :draggable="!readOnly"
    @dragstart="$emit('drag-start', $event)"
    @dragenter="$emit('drag-enter', $event)"
    @dragover.prevent
    @dragend="$emit('drag-end')"
    @drop.prevent="$emit('drop')"
    )
    ._cell(:style="s('check')")
      input._check(type="checkbox" data-testid="spec-row-checkbox" :checked="selected" @change="$emit('select', $event.target.checked)")
    ._cell(:style="s('drag')")
      span._drag(v-if="!readOnly" data-testid="spec-row-drag" title="Перетащить") ⠿
      span._readonly-mark(v-if="isAccepted && !unlocked" data-testid="spec-row-readonly" title="Позиция принята: только чтение")
      //- Маркер перехода «к позиции» (ТЗ 6.8, 6.10): есть в разметке только у той строки, к которой
      //- привёл переход по ссылке из уведомления или из события чата
      span._pointed-mark(
        v-if="highlighted"
        data-testid="spec-row-highlighted"
        title="Вы перешли к этой позиции"
        ) ➜

    ._cell._photo(:style="s('photo')" @dragover.prevent @drop.prevent.stop="onPhotoDrop")
      ._photo-box(data-testid="spec-row-photo" @click.stop="$emit('photo-menu')")
        img._photo-img(v-if="photoUrl" data-testid="spec-row-photo-image" :src="photoUrl" :alt="item.name")
        Icon._photo-icon(v-else name="no-image")
      SpecPopover(v-if="photoMenuOpen" data-testid="spec-photo-menu")
        button.spec-popover-item(v-if="photoUrl" type="button" data-testid="spec-photo-enlarge" @click="pick('photo-enlarge')") Увеличить
        label.spec-popover-item(v-if="!isReadOnly")
          | Заменить
          input._file(type="file" accept="image/*" data-testid="spec-photo-replace-input" @change="onPhotoFile")
        button.spec-popover-item(v-if="!isReadOnly" type="button" data-testid="spec-photo-find" @click="pick('find-photo')") Найти

    ._cell(:style="s('name')")
      SpecCell(
        data-testid="spec-row-name"
        :value="item.name"
        :display="item.name"
        :readOnly="isReadOnly"
        :active="isEditing('name')"
        @edit="$emit('edit', 'name')"
        @close="$emit('edit', null)"
        @save="save('name', $event)"
        )
    ._cell(:style="s('expand')")
      button._expand(type="button" data-testid="spec-row-expand" @click="$emit('expand')") Отклики: {{ offersCount }}

    ._cell(v-for="field in textFields" :key="field.key" :style="s(field.key)")
      SpecCell(
        :data-testid="field.testId"
        :value="item[field.key]"
        :display="item[field.key] || ''"
        :readOnly="isReadOnly"
        :active="isEditing(field.key)"
        @edit="$emit('edit', field.key)"
        @close="$emit('edit', null)"
        @save="save(field.key, $event)"
        )

    ._cell(:style="s('room')")
      select._select(data-testid="spec-row-room" :disabled="isReadOnly" :value="item.room || ''" @change="save('room', $event.target.value || null)")
        option(value="" data-testid="spec-room-option") — не задано —
        option(v-for="room in roomOptions" :key="room" :value="room" data-testid="spec-room-option") {{ room }}

    ._cell(:style="s('quantity')")
      SpecCell(
        data-testid="spec-row-quantity"
        :value="item.quantity"
        :display="quantityText"
        :readOnly="isReadOnly"
        :active="isEditing('quantity')"
        @edit="$emit('edit', 'quantity')"
        @close="$emit('edit', null)"
        @save="save('quantity', $event)"
        )
    ._cell._locked(:style="s('quantityFact')" data-testid="spec-row-quantity-fact" title="Количество из выбранного отклика")
      | {{ quantity(item.quantityFact) }}
      span._lock(v-if="item.quantityFact") 🔒
    ._cell(:style="s('unit')")
      select._select(data-testid="spec-row-unit" :disabled="isReadOnly" :value="item.unit" @change="save('unit', $event.target.value)")
        option(v-for="(label, code) in UNITS" :key="code" :value="code" data-testid="spec-unit-option") {{ label }}

    ._cell._delivery(:style="s('delivery')")
      ._delivery-value(data-testid="spec-row-delivery" :class="{ editable: canEditDelivery }" @click="onDeliveryClick")
        span {{ deliveryValue }}
        span._lock(v-if="item.chosenOffer" data-testid="spec-row-delivery-locked" title="Вносится поставщиком в отклике") 🔒
      SpecDeliveryEditor(
        v-if="deliveryEditor"
        :value="item.deliveryTerm"
        @save="onDeliverySave"
        @close="$emit('delivery-editor', false)"
        )

    ._cell(:style="s('comment')")
      SpecCell(
        data-testid="spec-row-comment"
        :value="item.designerComment"
        :display="item.designerComment || ''"
        :readOnly="isReadOnly"
        :active="isEditing('designerComment')"
        @edit="$emit('edit', 'designerComment')"
        @close="$emit('edit', null)"
        @save="save('designerComment', $event)"
        )

    ._cell._planned(:style="s('plannedPrice')")
      SpecCell(
        data-testid="spec-row-planned-price"
        :value="item.plannedPrice"
        :display="money(item.plannedPrice, '')"
        :readOnly="isReadOnly"
        :active="isEditing('plannedPrice')"
        @edit="$emit('edit', 'plannedPrice')"
        @close="$emit('edit', null)"
        @save="save('plannedPrice', $event)"
        )
        span._sources(
          v-if="hasSources"
          data-testid="spec-row-planned-sources"
          title="Источники плановой цены"
          @click.stop="$emit('sources')"
          ) 🔗
        span._low(
          v-if="item.plannedPriceLowConfidence"
          data-testid="spec-row-planned-low-confidence"
          title="Цена ориентировочная"
          ) ⚠
        button._analog(
          v-if="analog"
          type="button"
          data-testid="spec-row-analog"
          title="AI нашёл более дешёвый аналог"
          @click.stop="$emit('analog')"
          ) аналог −{{ money(analog.saving, '') }}
        SpecPopover(v-if="analogOpen && analog" data-testid="spec-analog-popover" @click.native.stop)
          ._analog-box
            ._analog-title Аналог дешевле
            ._analog-line(data-testid="spec-analog-name") {{ item.name }} → {{ analogTitle }}
            ._analog-line(data-testid="spec-analog-price")
              | Цена: {{ money(analog.price) }} вместо {{ money(item.plannedPrice) }}
              |  (×{{ quantityText }} = −{{ money(analog.saving) }})
            ._analog-line(v-if="analog.difference") Чем отличается: {{ analog.difference }}
            a.spec-popover-item.wrap(
              v-for="(source, i) in analog.sources"
              :key="i"
              :href="source.url"
              target="_blank"
              rel="noopener noreferrer"
              ) {{ source.url }} — {{ money(source.price, '') }}
            ._analog-buttons(v-if="!isReadOnly")
              Btn(blue xs data-testid="spec-analog-apply" @click="pick('analog-apply')") Заменить
              Btn(grey xs data-testid="spec-analog-reject" @click="pick('analog-reject')") Отклонить
        SpecPopover(v-if="sourcesOpen" data-testid="spec-sources-popover" @click.native.stop)
          a.spec-popover-item.wrap(
            v-for="(source, i) in item.plannedPriceSources"
            :key="i"
            data-testid="spec-sources-link"
            :href="source.url"
            target="_blank"
            rel="noopener noreferrer"
            ) {{ source.url }} — {{ money(source.price, '') }}

    ._cell._locked(:style="s('supplierPrice')" data-testid="spec-row-supplier-price" title="Вносится поставщиком в отклике")
      | {{ money(item.supplierPrice, '') }}
      span._lock 🔒
    ._cell._locked(:style="s('discount')" data-testid="spec-row-discount" title="Вносится поставщиком в отклике")
      | {{ money(item.unitDiscount, '') }}
      span._lock 🔒
    ._cell(:style="s('customerDiscount')" data-testid="spec-row-customer-discount")
      | {{ money(item.customerDiscount, '') }}
    ._cell(:style="s('customerPrice')" data-testid="spec-row-customer-price" :class="deviationClass" :title="deviationHint")
      | {{ money(item.customerPrice, '') }}
    ._cell(:style="s('customerTotal')" data-testid="spec-row-customer-total") {{ money(item.customerTotal, '') }}
    ._cell(:style="s('reward')" data-testid="spec-row-reward") {{ money(item.designerRewardTotal, '') }}
    ._cell(:style="s('commission')" data-testid="spec-row-commission") {{ money(item.commissionTotal, '') }}
    ._cell(:style="s('income')" data-testid="spec-row-income") {{ money(item.designerIncome, '') }}
    ._cell(:style="s('planDeviation')" data-testid="spec-row-plan-deviation") {{ deviationText }}
    ._cell(:style="s('supplier')" data-testid="spec-row-supplier") {{ item.supplierName || '' }}
    ._cell(:style="s('rewardReceived')" data-testid="spec-row-reward-received") {{ date(item.rewardReceivedAt, '') }}
    ._cell(:style="s('status')" data-testid="spec-row-status") {{ statusText }}

    ._cell._actions(:style="s('actions')")
      Btn(v-if="!readOnly && item.status === 'supplier_confirmed'" blue xs data-testid="spec-row-contract" @click="$emit('transition', 'contract')") Договор
      Btn(v-if="!readOnly && item.status === 'contract'" blue xs data-testid="spec-row-ordered" @click="$emit('transition', 'ordered')") Заказано
      ._menu
        button._menu-btn(type="button" data-testid="spec-row-menu" @click.stop="$emit('menu')") ⋯
        SpecPopover(v-if="menuOpen" align="right")
          button.spec-popover-item(v-if="canRequest" type="button" data-testid="spec-row-menu-request" @click="pick('request')") Запросить КП
          button.spec-popover-item(v-if="!readOnly" type="button" data-testid="spec-row-menu-find-price" @click="pick('find-price')") Найти цену заново
          button.spec-popover-item(v-if="!readOnly" type="button" data-testid="spec-row-menu-find-photo" @click="pick('find-photo')") Найти фото
          button.spec-popover-item(type="button" data-testid="spec-row-menu-chat" @click="pick('chat')") Открыть чат
          button.spec-popover-item(v-if="canReset" type="button" data-testid="spec-row-menu-reset" @click="pick('reset')") Вернуть в исходное состояние
          button.spec-popover-item(v-if="!readOnly && isAccepted" type="button" data-testid="spec-row-menu-edit" @click="pick('edit')") Редактировать
          button.spec-popover-item(v-if="canDelete" type="button" data-testid="spec-row-menu-delete" @click="pick('delete')") Удалить
</template>

<script>
import SpecCell from '@/components/specification/SpecCell.vue';
import SpecDeliveryEditor from '@/components/specification/SpecDeliveryEditor.vue';
import SpecPopover from '@/components/specification/SpecPopover.vue';
import { colStyle } from '@/components/specification/columns';
import {
  UNITS,
  money,
  quantity,
  date,
  percent,
  deliveryText,
  itemStatusTitle,
  deviationLevel,
  toNumber,
  BLOCKED_FOR_REQUEST,
  NO_DELETE_STATUSES,
  RESETTABLE_STATUSES,
} from '@/utils/spec';

/**
 * Строка позиции спецификации со всеми столбцами 6.2. Ничего не сохраняет сама: правки уходят
 * наверх событием `save`, потому что запрос изменения позиции и обновление хранилища — дело
 * страницы (одно сохранение = один запрос, значения вычисляемых полей берутся из ответа).
 *
 * Меню действий — свои пункты в общей всплывающей панели (SpecPopover), а не EllipsisMenu:
 * сценариям нужна тестовая метка на каждом пункте, а пункты существуют в разметке только у
 * раскрытого меню одной строки (иначе метка пункта перестала бы быть единственной на экране,
 * ТЗ 12.8). Сгенерировано Claude.
 */
export default {
  name: 'SpecRow',
  components: { SpecCell, SpecDeliveryEditor, SpecPopover },
  props: {
    item: { type: Object, required: true },
    rooms: { type: Array, default: () => [] },
    selected: Boolean,
    /** Поле, которое сейчас правится в этой строке (в таблице оно одно на экран). */
    editing: String,
    menuOpen: Boolean,
    deliveryEditor: Boolean,
    photoMenuOpen: Boolean,
    sourcesOpen: Boolean,
    analogOpen: Boolean,
    /** Принятая позиция открыта на правку до ухода со страницы (ТЗ 6.2). */
    unlocked: Boolean,
    /** Архивный проект: действия записи скрыты, ячейки не редактируются (ТЗ 6.1, 6.9). */
    readOnly: Boolean,
    /** Строка, к которой привёл переход «к позиции» — из уведомления или из события чата (6.8, 6.10). */
    highlighted: Boolean,
  },
  data() {
    return {
      UNITS,
      textFields: [
        { key: 'brand', testId: 'spec-row-brand' },
        { key: 'series', testId: 'spec-row-series' },
        { key: 'article', testId: 'spec-row-article' },
        { key: 'colorMaterial', testId: 'spec-row-color' },
        { key: 'dimensions', testId: 'spec-row-dimensions' },
      ],
    };
  },
  computed: {
    isAccepted() { return this.item.status === 'accepted'; },
    isReadOnly() { return this.readOnly || (this.isAccepted && !this.unlocked); },
    canDelete() { return !this.readOnly && !NO_DELETE_STATUSES.includes(this.item.status); },
    /** «Вернуть в исходное состояние» — от согласования с заказчиком до договора (ТЗ 2.5). */
    canReset() { return !this.readOnly && RESETTABLE_STATUSES.includes(this.item.status); },
    canRequest() { return !this.readOnly && !BLOCKED_FOR_REQUEST.includes(this.item.status); },
    canEditDelivery() { return !this.isReadOnly && !this.item.chosenOffer; },
    offersCount() { return (this.item.offers ?? []).length; },
    photoUrl() { return this.item.photo?.thumbnails?.avatar ?? this.item.photo?.url ?? null; },
    hasSources() { return (this.item.plannedPriceSources ?? []).length > 0; },
    /** Более дешёвый аналог от AI; экономию на всё количество считает сервер. */
    analog() { return this.item.plannedPriceAnalog ?? null; },
    analogTitle() {
      const { name, brand, article } = this.analog ?? {};
      return [name, brand, article && `арт. ${article}`].filter((part) => !!part).join(', ');
    },
    quantityText() { return quantity(this.item.quantity); },
    deliveryValue() { return deliveryText(this.item.deliveryTerm, ''); },
    statusText() { return itemStatusTitle(this.item.status); },
    roomOptions() {
      const rooms = [...this.rooms];
      if (this.item.room && !rooms.includes(this.item.room)) rooms.push(this.item.room);
      return rooms;
    },
    deviationText() {
      const value = toNumber(this.item.planDeviation);
      if (value === null) return '';
      const share = this.item.planDeviationPercent;
      return share === null || share === undefined
        ? money(value)
        : `${money(value)} (${percent(share)})`;
    },
    /** Процент отклонения считает сервер (ТЗ 7.1): клиент только выбирает по нему цвет. */
    deviationClass() {
      const level = deviationLevel(this.item.planDeviationPercent);
      return level ? `deviation-${level}` : null;
    },
    deviationHint() {
      const share = toNumber(this.item.planDeviationPercent);
      if (!deviationLevel(share)) return null;
      const plan = money(this.item.plannedPrice);
      const fact = money(this.item.customerPrice);
      return `Цена для заказчика выше плановой на ${Math.round(share)} % (план ${plan}, для заказчика ${fact})`;
    },
  },
  methods: {
    money,
    quantity,
    date,
    s: colStyle,
    isEditing(field) { return this.editing === field; },
    save(field, value) {
      this.$emit('save', { field, value });
    },
    onDeliveryClick() {
      if (!this.canEditDelivery) return;
      this.$emit('delivery-editor', true);
    },
    onDeliverySave(term) {
      this.$emit('save', { field: 'deliveryTerm', value: term });
      this.$emit('delivery-editor', false);
    },
    pick(action) {
      this.$emit('action', action);
    },
    /** Перетаскивание файла в ячейку фото загружает фото (ТЗ 6.2). */
    onPhotoDrop(event) {
      if (this.isReadOnly) return;
      const [file] = Array.from(event.dataTransfer?.files ?? []);
      if (file) this.$emit('photo-file', file);
    },
    onPhotoFile(event) {
      const [file] = Array.from(event.target.files ?? []);
      if (file) this.$emit('photo-file', file);
    },
  },
};
</script>

<style scoped lang="scss">
.spec-row {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid var(--color-super-light);
  background: var(--color-white);
  font-size: 12px;

  &.selected {
    background: #F4F9FF;
  }

  &.readonly {
    background: #F5F6F8;
    color: var(--color-light-dark);
  }

  // строка, к которой привёл переход из уведомления или из события чата (ТЗ 6.8, 6.10)
  &.highlighted {
    background: #FFF7E0;
  }

  &__cell {
    box-sizing: border-box;
    padding: 6px 6px;
    border-right: 1px solid #F3F5F8;
    position: relative;
    overflow: visible;

    &.deviation-medium { background: #FFF6DC; }
    &.deviation-high { background: #FFE8E8; }
  }

  &__check {
    margin: 2px 0 0;
    width: 16px;
    height: 16px;
  }

  &__drag {
    cursor: grab;
    color: var(--color-light-dark);
    user-select: none;
  }

  &__readonly-mark {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-light-dark);
  }

  &__pointed-mark {
    display: block;
    font-size: 12px;
    line-height: 1;
    color: var(--color-accent);
  }

  &__photo-box {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--color-super-light);
    border-radius: 4px;
    cursor: pointer;
    overflow: hidden;
  }

  &__photo-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__photo-icon {
    width: 18px;
    height: 18px;
    fill: var(--color-light-dark);
  }

  &__expand {
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    color: var(--color-accent);
    cursor: pointer;
    text-align: left;
  }

  &__select {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid transparent;
    border-radius: 3px;
    background: transparent;
    font: inherit;
    font-size: 12px;
    padding: 1px 2px;

    &:hover:not(:disabled) {
      border-color: var(--color-purple);
    }
  }

  &__delivery-value {
    display: block;
    min-height: 20px;
    line-height: 20px;

    &.editable {
      cursor: pointer;
    }
  }

  &__lock {
    margin-left: 3px;
  }

  &__sources,
  &__low {
    margin-left: 4px;
    cursor: pointer;
  }

  &__analog {
    margin-left: 4px;
    padding: 1px 6px;
    border: none;
    border-radius: 10px;
    background: var(--color-light-green, #e6f6ec);
    color: var(--color-green, #1a7f45);
    font: inherit;
    font-size: 11px;
    white-space: nowrap;
    cursor: pointer;
  }

  &__analog-box {
    display: flex;
    flex-direction: column;
    gap: 6px;
    box-sizing: border-box;
    width: 360px;
    padding: 4px 8px;
    white-space: normal;
  }

  &__analog-title {
    font-weight: 700;
  }

  &__analog-buttons {
    display: flex;
    gap: 8px;
    margin-top: 4px;
  }

  &__file {
    position: absolute;
    opacity: 0;
    visibility: hidden;
  }

  &__actions {
    display: flex;
    align-items: flex-start;
    gap: 6px;
  }

  &__menu {
    position: relative;
    margin-left: auto;
  }

  &__menu-btn {
    border: none;
    background: none;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    padding: 0 4px;
  }

}
</style>
